import React from 'react';
import { MessageBubble as StyledMessageBubble, ContactOptionsContainer, ContactOptionButton } from './SalesAgent.styled';

// Define types inline to avoid import issues
interface SalesAgentMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  isError?: boolean;
  metadata?: {
    intent?: any;
    lead_score?: number;
    handoff_needed?: boolean;
    next_steps?: string[];
    contact_options?: ContactOption[];
  };
}

interface SalesAgentConfig {
  organization_name?: string;
  welcomeMessage?: string;
  product_info?: {
    name?: string;
    description?: string;
    features?: string[];
    benefits?: string[];
  };
  pricing_info?: {
    model?: string;
    starting_price?: string;
    tiers?: string[];
  };
  security_info?: {
    certifications?: string[];
    compliance?: string[];
    data_protection?: string;
  };
  contact_options?: {
    email?: string;
    phone?: string;
    calendar?: string;
    chat?: boolean;
  };
  handoff_criteria?: {
    score_threshold?: number;
    max_messages?: number;
  };
  sandbox_restrictions?: string[];
}

interface ContactOption {
  type: 'email' | 'phone' | 'calendar' | 'handoff' | 'chat';
  label: string;
  value: string;
  priority?: number;
}

interface MessageBubbleProps {
  message: SalesAgentMessage;
  config?: SalesAgentConfig;
  onContactOption?: (option: ContactOption) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, config, onContactOption }) => {
  const isUser = message.role === 'user';
  const isError = message.isError;

  const renderContactOptions = () => {
    if (!message.metadata?.contact_options || message.metadata.contact_options.length === 0) {
      return null;
    }

    return (
      <ContactOptionsContainer>
        {message.metadata.contact_options.map((option, index) => (
          <ContactOptionButton
            key={index}
            onClick={() => onContactOption?.(option)}
          >
            {option.label}
          </ContactOptionButton>
        ))}
      </ContactOptionsContainer>
    );
  };

  const renderNextSteps = () => {
    if (!message.metadata?.next_steps || message.metadata.next_steps.length === 0) {
      return null;
    }

    return (
      <div style={{ marginTop: '8px' }}>
        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>
          Next steps:
        </div>
        <ul style={{ margin: '0', paddingLeft: '16px', fontSize: '12px' }}>
          {message.metadata.next_steps.map((step, index) => (
            <li key={index}>{step}</li>
          ))}
        </ul>
      </div>
    );
  };

  const renderLeadScore = () => {
    if (!message.metadata?.lead_score) {
      return null;
    }

    const score = message.metadata.lead_score;
    let scoreColor = '#6c757d';
    let scoreText = 'New Lead';

    if (score >= 75) {
      scoreColor = '#28a745';
      scoreText = 'Highly Qualified';
    } else if (score >= 50) {
      scoreColor = '#ffc107';
      scoreText = 'Qualified';
    } else if (score >= 25) {
      scoreColor = '#fd7e14';
      scoreText = 'Warm Lead';
    }

    return (
      <div style={{ marginTop: '8px' }}>
        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>
          Lead Score:
        </div>
        <div style={{ 
          display: 'inline-block', 
          padding: '2px 8px', 
          background: scoreColor, 
          color: 'white', 
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: '500'
        }}>
          {scoreText} ({score}%)
        </div>
      </div>
    );
  };

  return (
    <StyledMessageBubble isUser={isUser} isError={isError}>
      <div>{message.content}</div>
      {!isUser && !isError && (
        <>
          {renderContactOptions()}
          {renderNextSteps()}
          {renderLeadScore()}
        </>
      )}
    </StyledMessageBubble>
  );
};

export default MessageBubble;
